import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { getSocket } from '../lib/socket';
import { Dialog } from './ui/Dialog';
import { Button } from './ui/Button';

interface MintResponse {
  token: string;
  expiresAt: string;
}

interface CapturedEvent {
  token: string;
  capturedUrl: string;
}

/**
 * "Scan receipt on phone" button + modal. Clicking mints a 15-minute
 * one-shot token from /workspaces/:wsId/scan/mint; the modal renders a
 * QR encoding the URL `${origin}/m/scan/:token`. Subscribes to the
 * gateway's `mobile-scan:captured` event so the desktop reacts the
 * moment the phone uploads.
 */
export function MobileScanQrButton({ workspaceId, reportId }: { workspaceId: string; reportId?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<MintResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [captured, setCaptured] = useState<string | null>(null);
  const qc = useQueryClient();

  useEffect(() => {
    if (!open) return;
    setData(null);
    setCaptured(null);
    setBusy(true);
    api.post<MintResponse>(`/workspaces/${workspaceId}/scan/mint`, { reportId })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  }, [open, workspaceId, reportId]);

  useEffect(() => {
    if (!open || !data) return;
    const socket = getSocket();
    function onCaptured(e: CapturedEvent) {
      if (e.token === data?.token) {
        setCaptured(e.capturedUrl);
        qc.invalidateQueries({ queryKey: ['expense-reports', workspaceId] });
      }
    }
    socket.on('mobile-scan:captured', onCaptured);
    return () => { socket.off('mobile-scan:captured', onCaptured); };
  }, [open, data, workspaceId, qc]);

  const url = data ? `${window.location.origin}/m/scan/${data.token}` : '';

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        <Smartphone size={14} /> {t('mobileScan.openCta')}
      </Button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('mobileScan.dialogTitle')}
        description={t('mobileScan.dialogBody')}
      >
        <div className="flex flex-col items-center py-2">
          {busy && !data ? (
            <div className="text-sm text-ink-500 py-10">{t('common.loading')}</div>
          ) : captured ? (
            <div className="text-center py-4">
              <div className="font-medium text-emerald-600">{t('mobileScan.received')}</div>
              <div className="text-xs text-ink-500 mt-1 break-all max-w-xs">{captured}</div>
              <Button className="mt-4" onClick={() => setOpen(false)}>
                <X size={14} /> {t('common.cancel')}
              </Button>
            </div>
          ) : data ? (
            <>
              <div className="rounded-lg bg-white p-3 border border-ink-200">
                <QRCodeSVG value={url} size={192} level="M" />
              </div>
              <div className="text-xs text-ink-500 mt-3 max-w-xs text-center break-all">
                {t('mobileScan.urlHint')}: <span className="font-mono">{url}</span>
              </div>
              <div className="text-[11px] text-ink-400 mt-2">
                {t('mobileScan.expiresHint', { time: new Date(data.expiresAt).toLocaleTimeString() })}
              </div>
            </>
          ) : (
            <div className="text-sm text-warn-600 py-6">{t('mobileScan.mintError')}</div>
          )}
        </div>
      </Dialog>
    </>
  );
}
